import React, { ReactNode, useMemo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useDashboardContext } from 'context/dashboard.context';
import usePrevious from 'hooks/usePrevious';
import { Typography, Link } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useBreadcrumbs, { BreadcrumbData } from 'use-react-router-breadcrumbs';
import { StoresContextProvider, useStoresContext } from 'pages/Stores/stores.context';
import { CampaignsContextProvider, useCampaignsContext } from 'pages/Campaigns/campaigns.context';
import { UsersContextProvider, useUsersContext } from 'context/users.context';
import {
  ActivitiesContextProvider,
  useActivitiesContext,
} from 'pages/Activities/activities.context';

// Modify this to control how many levels of history we're keeping track of
const HISTORY_DEPTH = 5;

interface LocationData {
  name: string | ReactNode;
  path: string;
}

const StyledLink = styled(Link)`
  font-size: 1rem;
  display: flex;
  align-items: center;
  padding-bottom: 1rem;
  color: black;
  overflow: hidden;
  cursor: pointer;

  svg {
    font-size: 1rem;
  }
`;

const BackNavigationLinkComponent = (): JSX.Element => {
  const breadcrumbs = useBreadcrumbs();
  const { storeId, campaignId, groupId, activityId } = useParams();
  const [previousLocationsArray, setPreviousLocationsArray] = useState<LocationData[]>([]);
  const [popFirstLocation, setPopFirstLocation] = useState<Boolean>(false);
  const { showSidebar, toggleShowSidebar } = useDashboardContext();
  const { getStoreById } = useStoresContext();
  const { getCampaignById } = useCampaignsContext();
  const { getUserGroupById } = useUsersContext();
  const { getActivityById } = useActivitiesContext();
  const location = useLocation();
  const navigate = useNavigate();

  const getLocationName = (breadcrumb: BreadcrumbData) => {
    const defaultLocationName = breadcrumb.breadcrumb;

    if (storeId) {
      const store = getStoreById(Number(storeId));
      return store?.name ?? defaultLocationName;
    } else if (activityId) {
      const activity = getActivityById(Number(activityId));
      return activity?.subject && activity?.subject.length ? activity?.subject : '(Activity)';
    } else if (campaignId) {
      const campaign = getCampaignById(Number(campaignId));
      return campaign?.name ?? defaultLocationName;
    } else if (groupId) {
      const group = getUserGroupById(Number(groupId));
      return group?.name ?? defaultLocationName;
    }

    return defaultLocationName;
  };

  const currentLocation = useMemo(
    () => ({
      name: getLocationName(breadcrumbs[breadcrumbs.length - 1]),
      path: location.pathname,
    }),
    [location.pathname.toString()]
  );

  const previousLocation = usePrevious(currentLocation);

  useEffect(() => {
    if (popFirstLocation) {
      setPreviousLocationsArray(previousLocationsArray.slice(1, HISTORY_DEPTH));
      setPopFirstLocation(false);
    } else if (previousLocation && previousLocation.path !== currentLocation.path) {
      setPreviousLocationsArray([
        previousLocation,
        ...previousLocationsArray.slice(0, HISTORY_DEPTH),
      ]);
    }
  }, [previousLocation, currentLocation]);

  const entityParamExists = () => Boolean(storeId || campaignId || groupId || activityId);
  const showBackLink = useMemo(
    () => previousLocationsArray.length && entityParamExists(),
    [previousLocationsArray]
  );

  return showBackLink ? (
    <StyledLink
      onClick={() => {
        setPopFirstLocation(true);
        navigate(previousLocationsArray[0]!.path);
        if (showSidebar) toggleShowSidebar();
      }}
    >
      <ArrowBackIcon />
      <Typography padding='.2em .2em'>Back to {previousLocationsArray[0]!.name}</Typography>
    </StyledLink>
  ) : (
    <></>
  );
};

const BackNavigationLink = () => (
  <UsersContextProvider>
    <StoresContextProvider>
      <ActivitiesContextProvider>
        <CampaignsContextProvider>
          <BackNavigationLinkComponent />
        </CampaignsContextProvider>
      </ActivitiesContextProvider>
    </StoresContextProvider>
  </UsersContextProvider>
);

export default BackNavigationLink;
